import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Gift, Calendar, Package, Loader2, Sparkles, Check
} from 'lucide-react';
import { toast } from 'sonner';

const DonazioneForm = ({ donatori, prodotti, onSave, onClose, API }) => {
  const [mode, setMode] = useState('ai');
  const [formData, setFormData] = useState({
    donatore_id: '', prodotto_id: '', prodotto_testo: '',
    quantita: 1, valore_stimato: 0, note: ''
  });
  const [loading, setLoading] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [categorization, setCategorization] = useState(null);

  const selectedProdotto = prodotti.find(p => p.id === formData.prodotto_id);

  useEffect(() => {
    if (selectedProdotto && mode === 'manual') {
      setFormData(prev => ({ ...prev, valore_stimato: selectedProdotto.valore_unitario * prev.quantita }));
    }
  }, [formData.prodotto_id, formData.quantita, selectedProdotto, mode]);

  useEffect(() => {
    if (categorization && mode === 'ai') {
      setFormData(prev => ({ ...prev, valore_stimato: categorization.valore_stimato * prev.quantita }));
    }
  }, [categorization, formData.quantita, mode]);

  const handleCategorize = async () => {
    if (!formData.prodotto_testo.trim()) { toast.error('Inserisci un prodotto'); return; }
    setCategorizing(true);
    try {
      const response = await axios.post(`${API}/ai/categorizza-prodotto`, { testo: formData.prodotto_testo });
      setCategorization(response.data);
      setFormData(prev => ({ ...prev, valore_stimato: response.data.valore_stimato * prev.quantita }));
    } catch (err) {
      toast.error('Errore nella categorizzazione');
    } finally {
      setCategorizing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        donatore_id: formData.donatore_id,
        quantita: parseInt(formData.quantita) || 1,
        valore_stimato: parseFloat(formData.valore_stimato) || 0,
        note: formData.note || null
      };
      if (mode === 'ai') {
        payload.prodotto_testo = formData.prodotto_testo;
        if (categorization?.prodotto_esistente_id) payload.prodotto_id = categorization.prodotto_esistente_id;
      } else {
        payload.prodotto_id = formData.prodotto_id;
      }
      await onSave(payload);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Donatore *</Label>
        <Select value={formData.donatore_id} onValueChange={(v) => setFormData({ ...formData, donatore_id: v })}>
          <SelectTrigger data-testid="donazione-donatore-select"><SelectValue placeholder="Seleziona donatore" /></SelectTrigger>
          <SelectContent>{donatori.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome_donatore}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Tabs value={mode} onValueChange={setMode} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="ai" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>Smart</span>
          </TabsTrigger>
          <TabsTrigger value="manual" className="text-xs sm:text-sm">Manuale</TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="space-y-3 mt-3">
          <div className="space-y-2">
            <Label>Cosa e stato donato? *</Label>
            <div className="flex gap-2">
              <Input
                placeholder="es. pasta, olio, pannolini taglia 3..."
                value={formData.prodotto_testo}
                onChange={(e) => { setFormData({ ...formData, prodotto_testo: e.target.value }); setCategorization(null); }}
                data-testid="donazione-prodotto-testo"
              />
              <Button type="button" variant="outline" onClick={handleCategorize} disabled={categorizing || !formData.prodotto_testo.trim()} className="flex-shrink-0">
                {categorizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Scrivi il nome dell'alimento (senza marca). Per i pannolini specifica la taglia.</p>
          </div>

          {categorization && (
            <div className="p-3 sm:p-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                <span className="font-medium text-sm text-green-800 dark:text-green-200">Prodotto riconosciuto!</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                <div><span className="text-muted-foreground">Nome:</span><p className="font-medium">{categorization.nome_prodotto}</p></div>
                <div><span className="text-muted-foreground">Categoria:</span><Badge variant="secondary" className="ml-1 text-xs">{categorization.categoria}</Badge></div>
                {categorization.taglia_pannolino && (
                  <div className="col-span-2"><span className="text-muted-foreground">Taglia:</span><Badge variant="outline" className="ml-1 text-xs">{categorization.taglia_pannolino}</Badge></div>
                )}
                <div><span className="text-muted-foreground">Valore:</span><p className="font-medium">{categorization.valore_stimato.toFixed(2)}/u</p></div>
                {categorization.prodotto_esistente_id && <div className="col-span-2"><Badge variant="default" className="bg-blue-600 text-xs">Gia in magazzino</Badge></div>}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="manual" className="space-y-3 mt-3">
          <div className="space-y-2">
            <Label>Prodotto *</Label>
            <Select value={formData.prodotto_id} onValueChange={(v) => setFormData({ ...formData, prodotto_id: v })}>
              <SelectTrigger data-testid="donazione-prodotto-select"><SelectValue placeholder="Seleziona prodotto esistente" /></SelectTrigger>
              <SelectContent>{prodotti.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome_prodotto}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </TabsContent>
      </Tabs>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Quantita *</Label>
          <Input type="number" min="1" value={formData.quantita} onChange={(e) => setFormData({ ...formData, quantita: parseInt(e.target.value) || 1 })} data-testid="donazione-qta-input" />
        </div>
        <div className="space-y-2">
          <Label>Valore Tot.</Label>
          <Input type="number" step="0.01" min="0" value={formData.valore_stimato} onChange={(e) => setFormData({ ...formData, valore_stimato: parseFloat(e.target.value) || 0 })} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Note</Label>
        <Textarea value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} rows={2} />
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">Annulla</Button>
        <Button type="submit" disabled={loading || !formData.donatore_id || (mode === 'ai' ? !categorization : !formData.prodotto_id)} className="w-full sm:w-auto" data-testid="donazione-submit-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Registra Donazione
        </Button>
      </DialogFooter>
    </form>
  );
};

const DonazioneMobileCard = ({ d }) => (
  <div className="mobile-card-item" data-testid={`donazione-card-${d.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{d.donatore_nome}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{d.prodotto_nome}</Badge>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(d.data).toLocaleDateString('it-IT')}</span>
          <span>Qty: {d.quantita}</span>
        </div>
      </div>
      <span className="font-medium text-sm flex-shrink-0">{(d.valore_stimato || 0).toFixed(2)}</span>
    </div>
  </div>
);

export const DonazioniPage = () => {
  const { API, isAdmin } = useAuth();
  const [donazioni, setDonazioni] = useState([]);
  const [donatori, setDonatori] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [donazioniRes, donatoriRes, prodottiRes] = await Promise.all([
        axios.get(`${API}/donazioni`), axios.get(`${API}/donatori`), axios.get(`${API}/prodotti`)
      ]);
      setDonazioni(donazioniRes.data);
      setDonatori(donatoriRes.data);
      setProdotti(prodottiRes.data);
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    await axios.post(`${API}/donazioni`, data);
    toast.success('Donazione registrata');
    fetchData();
  };

  const getTotaleValore = () => donazioni.reduce((sum, d) => sum + (d.valore_stimato || 0), 0);

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="donazioni-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Donazioni</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Registra e visualizza le donazioni ricevute</p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <Button onClick={() => setDialogOpen(true)} className="w-full sm:w-auto" data-testid="add-donazione-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Donazione
            </Button>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Registra Donazione</DialogTitle>
                <DialogDescription>Inserisci il nome dell'alimento e il sistema lo categorizza automaticamente</DialogDescription>
              </DialogHeader>
              <DonazioneForm donatori={donatori} prodotti={prodotti} onSave={handleSave} onClose={() => setDialogOpen(false)} API={API} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6">
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Totale</p>
                <p className="text-xl sm:text-2xl font-heading font-bold">{donazioni.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-600/10 flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Valore</p>
                <p className="text-xl sm:text-2xl font-heading font-bold">{getTotaleValore().toFixed(0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2 sm:col-span-1">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Donatori</p>
                <p className="text-xl sm:text-2xl font-heading font-bold">{donatori.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
          <CardTitle className="text-sm sm:text-base">Storico Donazioni</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 sm:p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : donazioni.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <Gift className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">Nessuna donazione registrata</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Donatore</TableHead>
                      <TableHead>Prodotto</TableHead>
                      <TableHead className="text-center">Quantita</TableHead>
                      <TableHead className="text-right">Valore</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {donazioni.map((d) => (
                      <TableRow key={d.id} data-testid={`donazione-row-${d.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-muted-foreground" />{new Date(d.data).toLocaleDateString('it-IT')}</div>
                        </TableCell>
                        <TableCell><div className="font-medium">{d.donatore_nome}</div></TableCell>
                        <TableCell><Badge variant="outline">{d.prodotto_nome}</Badge></TableCell>
                        <TableCell className="text-center font-medium">{d.quantita}</TableCell>
                        <TableCell className="text-right font-medium">{(d.valore_stimato || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* Mobile Card List */}
              <div className="md:hidden">
                {donazioni.map((d) => <DonazioneMobileCard key={d.id} d={d} />)}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DonazioniPage;
